/** Seconds -> "MM:SS", e.g. 28 -> "00:28". Clamps negatives to "00:00". */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Whole seconds remaining until `timestamp`, never negative. */
export function secondsUntil(timestamp: number, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((timestamp - now) / 1000));
}

/* -- calendar days ---------------------------------------------------------- */

/**
 * Calendar days, as a form's date fields mean them.
 *
 * A financial year starts on **1 April**. It does not start at an instant — it starts on a day somebody
 * picked off a calendar. But the date control hands back a `Date`, a form stores it as an ISO instant, and
 * the API answers `"2026-04-01"`, so the same day arrives in three shapes and two of them shift across
 * midnight whenever the browser is not on UTC.
 *
 * Both directions had already gone wrong here, in opposite ways:
 *
 * - `new Date("2026-04-01")` is UTC midnight, whose local day is **31 March** west of Greenwich — so a
 *   record read back for editing showed the day before the one it was saved with.
 * - `date.toISOString().slice(0, 10)` on local midnight of 1 April in IST is **2026-03-31** — so a
 *   `LocalDate` column was written with the day before the one the user picked.
 *
 * Everything below therefore works in the browser's own zone, which is the calendar the user was reading.
 *
 * Shared rather than per-feature on purpose: Organization Setup's Add Entity dialog and the Entity master's
 * record form write the same two columns, and had drifted into two different wrong answers.
 */

/** A `yyyy-MM-dd` value, as the API's `LocalDate` fields carry one. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DD_MM_YYYY = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;

/** Midnight this morning, locally. A form's notion of "today". */
export function today(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * The calendar day a stored value falls on, whatever shape it is stored in.
 *
 * A date-only string is read as that day directly rather than through `Date.parse`, which would treat it as
 * UTC midnight and hand back the previous day in any western zone.
 */
export function toCalendarDay(value: string | null | undefined): Date | null {
  if (!value) return null;

  if (DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined) return null;
    return new Date(year, month - 1, day);
  }

  if (DD_MM_YYYY.test(value)) {
    const match = value.match(DD_MM_YYYY);
    if (match) {
      const [, dayStr, monthStr, yearStr] = match;
      let year = Number(yearStr);
      if (year < 100) year += 2000;
      const month = Number(monthStr);
      const day = Number(dayStr);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return new Date(year, month - 1, day);
      }
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** `yyyy-MM-dd` for the local calendar day of a stored value, which is what a `LocalDate` field takes. */
export function toApiDate(value: string | null | undefined): string {
  const day = toCalendarDay(value);
  if (day === null) return '';

  const year = day.getFullYear();
  const month = String(day.getMonth() + 1).padStart(2, '0');
  const date = String(day.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${date}`;
}

/** The day `count` days after `day`, at local midnight. Used for "the earliest the year may end". */
export function addDays(day: Date, count: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + count);
}

/** How a form stores a day it has just been given. */
export function toFormValue(day: Date): string {
  return day.toISOString();
}

/**
 * The day a calendar handed back, as the API's date-only fields take it: `yyyy-MM-dd`, the local day the user
 * picked (2026-10-14). Never `toISOString()`, whose UTC instant of local midnight is the day before east of
 * Greenwich -- 1 April picked in IST went out as `2026-03-31T18:30:00.000Z`.
 */
export function calendarDayToApi(day: Date): string {
  const month = String(day.getMonth() + 1).padStart(2, '0');
  const date = String(day.getDate()).padStart(2, '0');
  return `${String(day.getFullYear())}-${month}-${date}`;
}

/**
 * Today on the calendar of `zone` -- the entity's configured zone -- as a local-midnight `Date` a calendar can take
 * as its earliest day (2026-10-14). The server refuses a Delivery Date before the entity's today, so the calendar
 * offers the same days: at 21:00 in New York it is already tomorrow in India, and an Indian entity's request cannot
 * be delivered "today" by the browser's clock.
 *
 * No zone, or one the browser does not know, is the browser's own today.
 */
export function todayInZone(zone: string | null | undefined, now: Date = new Date()): Date {
  if (zone) {
    try {
      /* en-CA writes the date as yyyy-MM-dd. */
      const day = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now);
      const parsed = toCalendarDay(day);
      if (parsed) return parsed;
    } catch {
      /* An unknown zone: the browser's own calendar below. */
    }
  }
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * The date pattern a calendar shows a day in when the entity and the site configuration name none: the
 * platform's default row (`os_date_format_master.is_default`, `DD/MM/YYYY`).
 */
export const DEFAULT_CALENDAR_DATE_PATTERN = 'DD/MM/YYYY';

/**
 * An entity's date pattern (`DD/MM/YYYY`, `DD-MMM-YY`, `YYYY-MM-DD` ...) in PrimeReact Calendar's own tokens
 * (`dd/mm/yy`, `dd-M-y`, `yy-mm-dd`), so a calendar draws the day in the format the entity is configured with
 * (2026-10-14). The two spell the same parts differently -- the Calendar's `yy` is a four-digit year and its `M`
 * a short month name -- so the tokens are translated, never copied.
 *
 * A pattern holding no recognisable token falls back to the platform default.
 */
export function calendarDateFormat(pattern: string | null | undefined): string {
  const source = pattern?.trim() ? pattern.trim() : DEFAULT_CALENDAR_DATE_PATTERN;
  const tokens: Record<string, string> = { YYYY: 'yy', YY: 'y', MMMM: 'MM', MMM: 'M', MM: 'mm', DD: 'dd' };
  let matched = false;
  const translated = source.replace(/YYYY|YY|MMMM|MMM|MM|DD/g, (token) => {
    matched = true;
    return tokens[token] ?? token;
  });
  return matched ? translated : calendarDateFormat(DEFAULT_CALENDAR_DATE_PATTERN);
}

/*
  Re-exported, not re-implemented. Drawing a date is now a question about the entity's configured format,
  which is a bigger subject than this file's calendar arithmetic -- so it lives in `displayFormat.ts` and
  is surfaced here because every caller already imports dates from this module.
*/
export { formatStoredDateTime } from './displayFormat';
export type { DisplayFormat } from './displayFormat';
